import React from 'react';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { useNoAccountModal } from '@sparknz/journeys-common';
import { trackCustomLink } from '@sparknz/tracking';

import { IfpAccountSelectorQuery } from '__generated__/IfpAccountSelectorQuery.graphql';
import {
  PLAN_STEP,
  UPFRONT_PAYMENT_ID,
} from 'journey/productDetails/constants/ifp.constants';
import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  IfpEligibilityCheckSteps,
} from 'journey/productDetails/typings';
import { updateStepSections } from 'journey/productDetails/utils/ifp.utils';
import { useCartCookie, useQuery, useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import EligibilityAccountSelector from '../EligibilityAccountSelector';
import { useTrackAccountSelectorActionLayer } from '../IfpExistingPlanSelector/hooks';
import useTrackIfpAccountSelector from './useTrackIfpAccountSelector/useTrackIfpAccountSelector';

const ifpAccountSelectorQuery = graphql`
  query IfpAccountSelectorQuery(
    $cartId: String
    $channel: CartChannel!
    $deviceOfferId: String!
    $ifpOfferId: String!
  ) {
    me {
      ifpEligibility(
        cartId: $cartId
        channel: $channel
        deviceOfferId: $deviceOfferId
        ifpOfferId: $ifpOfferId
      ) {
        accounts {
          accountNumber
          accountName
          isEligible
        }
        eligibleLines {
          accountNumber
          accountName
          lineNumber
          offerId
          offerName
        }
        ineligibleLines {
          accountNumber
          accountName
          lineNumber
          offerId
          offerName
          reason
        }
      }
    }
  }
`;

const IfpAccountSelector = () => {
  const { cartId } = useCartCookie();
  const {
    dispatch,
    selectedDevice,
    planOffers,
    ifpEligibility: { sections },
  } = useProductDetails();
  const { channel } = useSegment();
  const { query, replaceQuery } = useQuery();
  const router = useRouter();
  const ifpId = extractFirstValue(router.query.ifpId);
  const { openNoAccountModal } = useNoAccountModal();

  const {
    me: { ifpEligibility },
  } = useLazyLoadQuery<IfpAccountSelectorQuery>(
    ifpAccountSelectorQuery,
    {
      cartId: cartId || null,
      channel,
      deviceOfferId: selectedDevice.id,
      ifpOfferId: ifpId,
    },
    {
      fetchPolicy: 'network-only',
    },
  );

  const { accounts, eligibleLines, ineligibleLines } = ifpEligibility;

  useTrackAccountSelectorActionLayer();

  useTrackIfpAccountSelector(accounts.length);

  const handleAccountSelection = (accountNumber: string | null) => {
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.LineSelection,
        selectedAccountNumber: accountNumber,
        eligibility: {
          ...ifpEligibility,
          eligibleLines: eligibleLines?.filter(
            (line) => line.accountNumber === accountNumber,
          ),
          ineligibleLines: ineligibleLines?.filter(
            (line) => line.accountNumber === accountNumber,
          ),
        },
        sections: updateStepSections([...sections, PLAN_STEP]),
      },
      actionLayerStatus: ActionLayerStatus.ShowExistingPlan,
    });
    trackCustomLink('Continue');
  };

  const handleNoAccounts = () => {
    dispatch({
      type: ProductDetailsActionTypes.SetActionLayer,
      actionLayerStatus: ActionLayerStatus.Close,
    });

    openNoAccountModal({
      onContinue() {
        dispatch({
          type: ProductDetailsActionTypes.SetActionLayer,
          actionLayerStatus: ActionLayerStatus.ShowExistingPlan,
          ifpEligibility: {
            activeStep: IfpEligibilityCheckSteps.AccountSelection,
            isChangePlan: false,
          },
        });
      },
      hideCheckout: true,
    });
  };

  if (isEmpty(planOffers) && !eligibleLines.length) {
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.DisableIfp,
        isCheckRequired: false,
      },
    });
    replaceQuery({ ...query, ifpId: UPFRONT_PAYMENT_ID });
    return null;
  }

  const filteredAccounts = accounts.filter(
    (account) => account.accountNumber !== null && account.accountNumber !== '',
  );

  return (
    <EligibilityAccountSelector
      accounts={filteredAccounts}
      onAccountSelection={handleAccountSelection}
      handleNoAccounts={handleNoAccounts}
    />
  );
};

export default IfpAccountSelector;
